import { useState, useEffect, useMemo, useRef } from 'react';
import { DateTime } from 'luxon';
import {
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  X,
} from 'lucide-react';
import BookingSidePanel from './BookingSidePanel';

const API_BASE_URL =
  'https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150/bookings';

const CALENDAR_ENDPOINT =
  `${API_BASE_URL}/viewAllbookingmonth`;

const getAccessToken = () =>
  sessionStorage.getItem('access_token');

const fetchAuth0Users = () => {
  const userMap = new Map();

  try {
    const storedUser = JSON.parse(
      sessionStorage.getItem('user') || '{}'
    );

    if (storedUser.email) {
      const username =
        storedUser.username ||
        storedUser.name ||
        storedUser.email.split('@')[0];

      userMap.set(
        storedUser.email,
        username
      );
    }
  } catch (e) {
    console.error(
      'Error reading user from sessionStorage:',
      e
    );
  }

  return userMap;
};

export default function BookingCalendar({
  onAddBooking,
  onSelectBooking,
}) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showSidePanel, setShowSidePanel] =
    useState(true);

  const [selectedBooking, setSelectedBooking] =
    useState(null);

  const [selectedDate, setSelectedDate] =
    useState(new Date());

  const [selectedTimeSlot, setSelectedTimeSlot] =
    useState(null);

  const [calendarView, setCalendarView] =
    useState('Month');

  const [currentViewDate, setCurrentViewDate] =
    useState(DateTime.now());

  const [showCancelledBookings, setShowCancelledBookings] =
    useState(false);

  const [hoveredDate, setHoveredDate] =
    useState(null);

  const [dayTooltipPos, setDayTooltipPos] =
    useState({
      x: 0,
      y: 0,
      showAbove: false,
    });

  const [isPanelCollapsed, setIsPanelCollapsed] =
    useState(false);

  const [
    showCancelledBookingModal,
    setShowCancelledBookingModal,
  ] = useState(false);

  const [dateRange, setDateRange] = useState({
    from: DateTime.now()
      .startOf('day')
      .toISODate(),

    to: DateTime.now()
      .plus({ days: 29 })
      .toISODate(),
  });

  const [calendarCache, setCalendarCache] =
    useState({});

  const calendarCacheRef =
    useRef({});

  const monthRequestsRef =
    useRef(new Map());

  const dateRangeCacheRef =
    useRef(new Map());

  const dateRangeRequestsRef =
    useRef(new Map());

  const fetchCalendarPage = async ({
    count,
    month,
    initialdate,
    finaldate,
    page = 1,
  }) => {
    const params =
      new URLSearchParams();

    params.set(
      'count',
      String(count)
    );

    params.set(
      'page',
      String(page)
    );

    params.set(
      'limit',
      '100'
    );

    if (count === 1) {
      params.set(
        'month',
        month
      );
    } else {
      params.set(
        'initialdate',
        initialdate
      );

      params.set(
        'finaldate',
        finaldate
      );
    }

    const response =
      await fetch(
        `${CALENDAR_ENDPOINT}?${params.toString()}`,
        {
          method: 'GET',

          headers: {
            'Content-Type':
              'application/json',

            Authorization:
              `Bearer ${getAccessToken()}`,
          },
        }
      );

    if (!response.ok) {
      throw new Error(
        `Failed to fetch calendar bookings: ${response.status}`
      );
    }

    return response.json();
  };

  const fetchAllCalendarPages =
    async (params) => {
      const firstPage =
        await fetchCalendarPage({
          ...params,
          page: 1,
        });

      const allBookings = [
        ...(firstPage.data || []),
      ];

      const totalPages =
        firstPage.meta?.totalPages || 1;

      if (totalPages > 1) {
        const remainingPages =
          await Promise.all(
            Array.from(
              {
                length:
                  totalPages - 1,
              },

              (_, index) =>
                fetchCalendarPage({
                  ...params,
                  page:
                    index + 2,
                })
            )
          );

        remainingPages.forEach(
          (pageData) => {
            allBookings.push(
              ...(pageData.data || [])
            );
          }
        );
      }

      return allBookings;
    };

  const processBookings = (
    rawBookings,
    userMap
  ) => {
    return rawBookings
      .map((booking) => {
        let email =
          booking.customer_id ||
          booking.customer_name ||
          booking.metadata
            ?.customer_name;

        if (
          booking.customer_name &&
          booking.customer_name.includes('@')
        ) {
          email =
            booking.customer_name;
        } else if (
          booking.metadata
            ?.customer_name &&
          booking.metadata.customer_name.includes('@')
        ) {
          email =
            booking.metadata.customer_name;
        }

        const displayName =
          email &&
          userMap.has(email)
            ? userMap.get(email)
            : (
                booking.metadata
                  ?.customer_name ||
                booking.customer_name ||
                booking.customer ||
                'Unknown User'
              );

        const tz =
          booking.location
            ?.time_zone ||
          'Asia/Jakarta';

        const startDt =
          booking.starts_at
            ? DateTime
                .fromISO(
                  booking.starts_at
                )
                .setZone(tz)
            : null;

        const endDt =
          booking.ends_at
            ? DateTime
                .fromISO(
                  booking.ends_at
                )
                .setZone(tz)
            : startDt
              ? startDt.plus({
                  minutes: 60,
                })
              : null;

        if (!startDt) {
          return null;
        }

        return {
          ...booking,

          customer_name:
            displayName,

          mongo_id:
            booking._id ||
            booking.mongo_id,

          calendarMeta: {
            tz,

            startDt,

            endDt,

            displayDate:
              startDt.toISODate(),

            displayTimeLabel:
              endDt
                ? `${startDt.toFormat(
                    'hh:mm a'
                  )} - ${endDt.toFormat(
                    'hh:mm a'
                  )}`
                : startDt.toFormat(
                    'hh:mm a'
                  ),

            startMinutes:
              startDt.hour * 60 +
              startDt.minute,

            endMinutes:
              endDt
                ? endDt.hour * 60 +
                  endDt.minute
                : startDt.hour * 60 +
                  startDt.minute +
                  60,
          },
        };
      })
      .filter(Boolean);
  };

  const saveMonthToCache = (
    month,
    monthBookings
  ) => {
    const merged =
      new Map();

    const existing =
      calendarCacheRef.current[
        month
      ] || [];

    existing.forEach(
      (booking) => {
        merged.set(
          booking.id,
          booking
        );
      }
    );

    monthBookings.forEach(
      (booking) => {
        merged.set(
          booking.id,
          booking
        );
      }
    );

    const finalBookings =
      Array.from(
        merged.values()
      );

    calendarCacheRef.current = {
      ...calendarCacheRef.current,

      [month]:
        finalBookings,
    };

    setCalendarCache(
      calendarCacheRef.current
    );

    return finalBookings;
  };

  const ensureMonthCached =
    async (month) => {
      if (
        Object.prototype.hasOwnProperty.call(
          calendarCacheRef.current,
          month
        )
      ) {
        return (
          calendarCacheRef.current[
            month
          ] || []
        );
      }

      if (
        monthRequestsRef.current.has(
          month
        )
      ) {
        return monthRequestsRef.current.get(
          month
        );
      }

      const request =
        (async () => {
          try {
            const userMap =
              fetchAuth0Users();

            const rawBookings =
              await fetchAllCalendarPages({
                count: 1,
                month,
              });

            const processed =
              processBookings(
                rawBookings,
                userMap
              );

            return saveMonthToCache(
              month,
              processed
            );
          } catch (err) {
            console.error(
              `Error loading month ${month}:`,
              err
            );

            throw err;
          } finally {
            monthRequestsRef.current.delete(
              month
            );
          }
        })();

      monthRequestsRef.current.set(
        month,
        request
      );

      return request;
    };

  const loadMonthWithNeighbors =
    async (date) => {
      const current =
        date.startOf('month');

      const previous =
        current.minus({
          months: 1,
        });

      const next =
        current.plus({
          months: 1,
        });

      const currentMonth =
        current.toFormat(
          'yyyy-MM'
        );

      const previousMonth =
        previous.toFormat(
          'yyyy-MM'
        );

      const nextMonth =
        next.toFormat(
          'yyyy-MM'
        );

      const currentData =
        await ensureMonthCached(
          currentMonth
        );

      Promise.all([
        ensureMonthCached(
          previousMonth
        ),

        ensureMonthCached(
          nextMonth
        ),
      ]).catch((err) => {
        console.error(
          'Error preloading adjacent months:',
          err
        );
      });

      return currentData;
    };

  const getMonthsForWeek =
    (date) => {
      const weekStart =
        date.startOf('week');

      const weekEnd =
        date.endOf('week');

      const months =
        new Set();

      let cursor =
        weekStart.startOf(
          'month'
        );

      while (
        cursor <= weekEnd
      ) {
        months.add(
          cursor.toFormat(
            'yyyy-MM'
          )
        );

        cursor =
          cursor.plus({
            months: 1,
          });
      }

      return Array.from(
        months
      );
    };

  const getCachedBookingsForMonths =
    (months) => {
      const merged =
        new Map();

      months.forEach(
        (month) => {
          const monthBookings =
            calendarCacheRef
              .current[
              month
            ] || [];

          monthBookings.forEach(
            (booking) => {
              merged.set(
                booking.id,
                booking
              );
            }
          );
        }
      );

      return Array.from(
        merged.values()
      );
    };

  const applyCancelledFilter =
    (source) => {
      if (
        showCancelledBookings
      ) {
        return source;
      }

      return source.filter(
        (booking) =>
          !booking.is_canceled
      );
    };

  const refreshCurrentView =
    async () => {
      try {
        setError(null);

        if (
          calendarView === 'Month'
        ) {
          const cached =
            await loadMonthWithNeighbors(
              currentViewDate
            );

          setBookings(
            applyCancelledFilter(
              cached || []
            )
          );

          return;
        }

        if (
          calendarView === 'Week'
        ) {
          const months =
            getMonthsForWeek(
              currentViewDate
            );

          await Promise.all(
            months.map(
              (month) =>
                ensureMonthCached(
                  month
                )
            )
          );

          const weekStart =
            currentViewDate.startOf(
              'week'
            );

          const weekEnd =
            currentViewDate.endOf(
              'week'
            );

          const previousMonth =
            weekStart
              .startOf('month')
              .minus({
                months: 1,
              })
              .toFormat(
                'yyyy-MM'
              );

          const nextMonth =
            weekEnd
              .startOf('month')
              .plus({
                months: 1,
              })
              .toFormat(
                'yyyy-MM'
              );

          Promise.all([
            ensureMonthCached(
              previousMonth
            ),

            ensureMonthCached(
              nextMonth
            ),
          ]).catch((err) => {
            console.error(
              'Error preloading week neighbors:',
              err
            );
          });

          const cached =
            getCachedBookingsForMonths(
              months
            );

          setBookings(
            applyCancelledFilter(
              cached
            )
          );

          return;
        }

        if (
          calendarView === 'Day'
        ) {
          const month =
            currentViewDate.toFormat(
              'yyyy-MM'
            );

          await loadMonthWithNeighbors(
            currentViewDate
          );

          const cached =
            getCachedBookingsForMonths(
              [month]
            );

          setBookings(
            applyCancelledFilter(
              cached
            )
          );

          return;
        }

        if (
          calendarView ===
          'Date Range'
        ) {
          if (
            !dateRange.from ||
            !dateRange.to
          ) {
            setBookings([]);

            return;
          }

          const cacheKey =
            `${dateRange.from}|${dateRange.to}`;

          if (
            dateRangeCacheRef
              .current
              .has(cacheKey)
          ) {
            const cached =
              dateRangeCacheRef
                .current
                .get(cacheKey);

            setBookings(
              applyCancelledFilter(
                cached
              )
            );

            return;
          }

          if (
            dateRangeRequestsRef
              .current
              .has(cacheKey)
          ) {
            const cached =
              await dateRangeRequestsRef
                .current
                .get(cacheKey);

            setBookings(
              applyCancelledFilter(
                cached
              )
            );

            return;
          }

          const from =
            DateTime
              .fromISO(
                dateRange.from
              )
              .startOf(
                'day'
              );

          const to =
            DateTime
              .fromISO(
                dateRange.to
              )
              .plus({
                days: 1,
              })
              .startOf(
                'day'
              );

          if (
            from >= to
          ) {
            setBookings([]);

            return;
          }

          const request =
            (async () => {
              try {
                const userMap =
                  fetchAuth0Users();

                const rawBookings =
                  await fetchAllCalendarPages({
                    count: 0,

                    initialdate:
                      from
                        .toUTC()
                        .toISO(),

                    finaldate:
                      to
                        .toUTC()
                        .toISO(),
                  });

                const processed =
                  processBookings(
                    rawBookings,
                    userMap
                  );

                dateRangeCacheRef
                  .current
                  .set(
                    cacheKey,
                    processed
                  );

                return processed;
              } finally {
                dateRangeRequestsRef
                  .current
                  .delete(
                    cacheKey
                  );
              }
            })();

          dateRangeRequestsRef
            .current
            .set(
              cacheKey,
              request
            );

          const processed =
            await request;

          setBookings(
            applyCancelledFilter(
              processed
            )
          );
        }
      } catch (err) {
        console.error(
          'Error refreshing calendar:',
          err
        );

        setError(
          'Failed to fetch calendar bookings'
        );
      }
    };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);

      try {
        await refreshCurrentView();

        if (cancelled) {
          return;
        }
      } catch (err) {
        if (!cancelled) {
          console.error(err);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [
    calendarView,
    currentViewDate,
    dateRange.from,
    dateRange.to,
    showCancelledBookings,
  ]);

  const timeSlots =
    useMemo(() => {
      const slots = [];

      for (
        let hour = 7;
        hour <= 23;
        hour++
      ) {
        slots.push({
          hour24: hour,

          label:
            DateTime
              .fromObject({
                hour,
              })
              .toFormat(
                'h a'
              ),
        });
      }

      return slots;
    }, []);

  const weekDates =
    useMemo(() => {
      const startOfWeek =
        currentViewDate.startOf(
          'week'
        );

      return Array.from(
        {
          length: 7,
        },
        (_, index) =>
          startOfWeek.plus({
            days: index,
          })
      );
    }, [
      currentViewDate,
    ]);

  const monthGridDates =
    useMemo(() => {
      if (
        calendarView !==
        'Month'
      ) {
        return [];
      }

      const startOfMonth =
        currentViewDate.startOf(
          'month'
        );

      const endOfMonth =
        currentViewDate.endOf(
          'month'
        );

      const startWeekday =
        startOfMonth.weekday;

      const daysToSubtract =
        startWeekday === 7
          ? 0
          : startWeekday;

      const startDate =
        startOfMonth.minus({
          days:
            daysToSubtract,
        });

      const endWeekday =
        endOfMonth.weekday;

      const daysToAdd =
        endWeekday === 7
          ? 6
          : 6 - endWeekday;

      const endDate =
        endOfMonth.plus({
          days:
            daysToAdd,
        });

      const dates = [];

      let cursor =
        startDate;

      const endMillis =
        endDate.toMillis();

      while (
        cursor.toMillis() <=
        endMillis
      ) {
        dates.push({
          dateTime:
            cursor,

          iso:
            cursor.toISODate(),

          label:
            cursor.day,

          isCurrentMonth:
            cursor.month ===
              currentViewDate.month &&
            cursor.year ===
              currentViewDate.year,
        });

        cursor =
          cursor.plus({
            days: 1,
          });
      }

      return dates;
    }, [
      calendarView,
      currentViewDate,
    ]);

  const getBookingsForDate =
    (isoDate) =>
      bookings.filter(
        (booking) =>
          booking.calendarMeta
            ?.displayDate ===
          isoDate
      );

  const getActiveBookingsForDate =
    (isoDate) =>
      bookings.filter(
        (booking) =>
          booking.calendarMeta
            ?.displayDate ===
            isoDate &&
          !booking.is_canceled
      );

  const getCancelledBookingsForDate =
    (isoDate) =>
      bookings.filter(
        (booking) =>
          booking.calendarMeta
            ?.displayDate ===
            isoDate &&
          booking.is_canceled
      );

  const isPastBooking =
    (booking) => {
      if (
        !booking?.calendarMeta
          ?.startDt
      ) {
        return false;
      }

      const now =
        DateTime.now().setZone(
          booking.calendarMeta
            .tz ||
            'Asia/Jakarta'
        );

      return (
        booking.calendarMeta
          .startDt < now
      );
    };

  const calendarHeaderText =
    useMemo(() => {
      if (
        calendarView === 'Week'
      ) {
        const start =
          currentViewDate.startOf(
            'week'
          );

        const end =
          currentViewDate.endOf(
            'week'
          );

        if (
          start.month ===
            end.month &&
          start.year ===
            end.year
        ) {
          return `${start.toFormat(
            'MMM d'
          )} - ${end.toFormat(
            'd, yyyy'
          )}`;
        }

        return `${start.toFormat(
          'MMM d'
        )} - ${end.toFormat(
          'MMM d, yyyy'
        )}`;
      }

      if (
        calendarView === 'Day'
      ) {
        return currentViewDate.toFormat(
          'MMMM d, yyyy'
        );
      }

      if (
        calendarView ===
        'Date Range'
      ) {
        if (
          !dateRange.from ||
          !dateRange.to
        ) {
          return 'Select Date Range';
        }

        const from =
          DateTime.fromISO(
            dateRange.from
          );

        const to =
          DateTime.fromISO(
            dateRange.to
          );

        if (
          from.month ===
            to.month &&
          from.year ===
            to.year
        ) {
          return `${from.toFormat(
            'MMM d'
          )} - ${to.toFormat(
            'd, yyyy'
          )}`;
        }

        return `${from.toFormat(
          'MMM d, yyyy'
        )} - ${to.toFormat(
          'MMM d, yyyy'
        )}`;
      }

      return currentViewDate.toFormat(
        'MMMM yyyy'
      );
    }, [
      calendarView,
      currentViewDate,
      dateRange,
    ]);

  const goToToday = () => {
    setCurrentViewDate(
      DateTime.now()
    );
  };

  const goToPrevious =
    () => {
      setCurrentViewDate(
        (prev) => {
          if (
            calendarView ===
            'Week'
          ) {
            return prev.minus({
              weeks: 1,
            });
          }

          if (
            calendarView ===
            'Month'
          ) {
            return prev.minus({
              months: 1,
            });
          }

          if (
            calendarView ===
            'Day'
          ) {
            return prev.minus({
              days: 1,
            });
          }

          return prev.minus({
            months: 1,
          });
        }
      );
    };

  const goToNext = () => {
    setCurrentViewDate(
      (prev) => {
        if (
          calendarView ===
          'Week'
        ) {
          return prev.plus({
            weeks: 1,
          });
        }

        if (
          calendarView ===
          'Month'
        ) {
          return prev.plus({
            months: 1,
          });
        }

        if (
          calendarView ===
          'Day'
        ) {
          return prev.plus({
            days: 1,
          });
        }

        return prev.plus({
          months: 1,
        });
      }
    );
  };

  const handleSlotSelection =
    (dateTime) => {
      const normalized =
        dateTime.set({
          second: 0,
          millisecond: 0,
        });

      setSelectedDate(
        normalized.toJSDate()
      );

      setSelectedTimeSlot(
        normalized.toFormat(
          'HH:mm'
        )
      );

      setSelectedBooking(
        null
      );

      setShowSidePanel(
        true
      );
    };

  const handleDayCellClick =
    (dateTime) => {
      handleSlotSelection(
        dateTime.set({
          hour: 9,
          minute: 0,
        })
      );
    };

  const handleEventSelect =
    (booking) => {
      if (
        isPastBooking(
          booking
        )
      ) {
        return;
      }

      if (
        booking.is_canceled
      ) {
        setShowCancelledBookingModal(
          true
        );

        return;
      }

      setSelectedBooking(
        booking
      );

      if (
        booking.starts_at
      ) {
        setSelectedDate(
          new Date(
            booking.starts_at
          )
        );
      }

      setSelectedTimeSlot(
        null
      );

      setShowSidePanel(
        true
      );

      if (
        isPanelCollapsed
      ) {
        setIsPanelCollapsed(
          false
        );
      }

      if (
        onSelectBooking
      ) {
        onSelectBooking(
          booking
        );
      }
    };

  const renderDayHoverTooltip =
    () => {
      if (
        !hoveredDate
      ) {
        return null;
      }

      const events =
        getBookingsForDate(
          hoveredDate
        );

      if (
        events.length === 0
      ) {
        return null;
      }

      const positionClass =
        dayTooltipPos.showAbove
          ? 'transform -translate-x-1/2 -translate-y-full mb-2'
          : 'transform -translate-x-1/2 mt-2';

      return (
        <div
          className={`fixed z-50 bg-white rounded-lg shadow-xl border border-gray-200 p-3 w-[90vw] sm:w-72 max-h-96 overflow-y-auto ${positionClass}`}
          style={{
            left:
              dayTooltipPos.x,

            top:
              dayTooltipPos.showAbove
                ? dayTooltipPos.y - 8
                : dayTooltipPos.y,
          }}
          onMouseLeave={() =>
            setHoveredDate(
              null
            )
          }
        >
          <div className="text-sm font-bold text-gray-900 mb-2">
            {events.length}{' '}
            {events.length === 1
              ? 'Booking'
              : 'Bookings'}
          </div>

          <div className="space-y-2">
            {events.map(
              (
                event,
                idx
              ) => {
                const isTemporary =
                  event.is_temporary;

                const isCancelled =
                  event.is_canceled;

                const isPast =
                  isPastBooking(
                    event
                  );

                const bgColor =
                  isCancelled
                    ? 'bg-red-50/70 border-red-100'
                    : isTemporary
                      ? 'bg-yellow-50 border-yellow-200'
                      : 'bg-blue-50 border-blue-200';

                return (
                  <button
                    key={`tooltip-${event.id}-${idx}`}
                    disabled={isPast}
                    onClick={(e) => {
                      e.stopPropagation();

                      handleEventSelect(
                        event
                      );

                      setHoveredDate(
                        null
                      );
                    }}
                    className={`w-full text-left text-xs rounded p-2 border ${bgColor} transition-all ${
                      isPast
                        ? 'cursor-not-allowed opacity-50'
                        : 'hover:shadow-md cursor-pointer hover:scale-[1.02]'
                    }`}
                  >
                    <div className="font-semibold text-gray-900">
                      {event.customer_name ||
                        'Unknown User'}
                    </div>

                    <div className="text-gray-600">
                      {event.resource
                        ?.name ||
                        'Resource'}
                    </div>

                    <div className="text-gray-500">
                      {
                        event
                          .calendarMeta
                          ?.displayTimeLabel
                      }
                    </div>
                  </button>
                );
              }
            )}
          </div>
        </div>
      );
    };

  const renderEventButton =
    (
      event,
      idx,
      isCompact
    ) => {
      const past =
        isPastBooking(
          event
        );

      const isTemporary =
        event.is_temporary;

      const isCancelled =
        event.is_canceled;

      return (
        <button
          key={`${event.id}-${idx}`}
          disabled={past}
          onClick={(e) => {
            e.stopPropagation();

            handleEventSelect(
              event
            );
          }}
          className={`w-full text-left text-xs border rounded px-2 py-1 focus:outline-none mb-1 ${
            past
              ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
              : isCancelled
                ? 'bg-red-50/70 border-red-100 hover:bg-red-50 text-gray-900'
                : isTemporary
                  ? 'bg-yellow-100/50 border-yellow-200 hover:bg-yellow-100/70 text-gray-900'
                  : 'bg-blue-50 border-blue-100 hover:bg-blue-100 text-gray-900'
          }`}
        >
          <div className="font-semibold text-gray-900">
            {event.customer_name ||
              'Unknown User'}
          </div>

          {!isCompact && (
            <>
              <div className="text-xs truncate">
                {event.resource
                  ?.name ||
                  'Resource'}
              </div>

              <div className="text-gray-600 truncate">
                {
                  event
                    .calendarMeta
                    ?.displayTimeLabel
                }
              </div>
            </>
          )}
        </button>
      );
    };

  const renderMonthView =
    () => (
      <div className="flex flex-col h-full relative">
        {renderDayHoverTooltip()}

        <div className="grid grid-cols-7 border-b border-gray-200 flex-shrink-0">
          {[
            'Sun',
            'Mon',
            'Tue',
            'Wed',
            'Thu',
            'Fri',
            'Sat',
          ].map((day) => (
            <div
              key={day}
              className="p-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wide"
            >
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 flex-1 auto-rows-fr">
          {monthGridDates.map(
            (dateInfo) => {
              const events =
                getBookingsForDate(
                  dateInfo.iso
                );

              const visibleEvents =
                events.slice(
                  0,
                  2
                );

              const hiddenCount =
                events.length > 2
                  ? events.length - 2
                  : 0;

              return (
                <div
                  key={
                    dateInfo.iso
                  }
                  onMouseEnter={(e) => {
                    const rect =
                      e.currentTarget.getBoundingClientRect();

                    const viewportHeight =
                      window.innerHeight;

                    const spaceBelow =
                      viewportHeight -
                      rect.bottom;

                    const tooltipEstimatedHeight = 300;

                    setHoveredDate(
                      dateInfo.iso
                    );

                    setDayTooltipPos({
                      x:
                        rect.left +
                        rect.width / 2,

                      y:
                        rect.bottom,

                      showAbove:
                        spaceBelow <
                          tooltipEstimatedHeight &&
                        rect.top >
                          tooltipEstimatedHeight,
                    });
                  }}
                  onClick={() =>
                    handleDayCellClick(
                      dateInfo.dateTime
                    )
                  }
                  className={`relative border-r border-b border-gray-200 p-1 md:p-2 min-h-[100px] md:min-h-[140px] cursor-pointer transition-colors ${
                    dateInfo.isCurrentMonth
                      ? 'bg-white hover:bg-blue-50/40'
                      : 'bg-gray-50 hover:bg-blue-50/40'
                  }`}
                >
                  <div
                    className="h-full flex flex-col"
                    style={{
                      paddingBottom:
                        '28px',
                    }}
                  >
                    <div
                      className={`text-sm font-semibold mb-2 ${
                        dateInfo.isCurrentMonth
                          ? 'text-gray-700'
                          : 'text-gray-400'
                      }`}
                    >
                      <span>
                        {
                          dateInfo.label
                        }
                      </span>
                    </div>

                    <div className="space-y-1">
                      {visibleEvents.map(
                        (
                          event,
                          idx
                        ) =>
                          renderEventButton(
                            event,
                            idx,
                            false
                          )
                      )}
                    </div>

                    {hiddenCount >
                      0 && (
                      <div className="absolute bottom-1 right-1 bg-gray-100 text-gray-600 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                        +
                        {
                          hiddenCount
                        }
                      </div>
                    )}
                  </div>
                </div>
              );
            }
          )}
        </div>
      </div>
    );

  const renderWeekView =
    () => {
      const startMinutes =
        7 * 60;

      const endMinutes =
        24 * 60;

      const hourHeight =
        64;

      const pxPerMinute =
        hourHeight / 60;

      return (
        <div className="overflow-x-auto">
          <div className="min-w-[800px]">
            <div className="grid grid-cols-8 border-b border-gray-200">
              <div className="p-4 border-r border-gray-200" />

              {weekDates.map(
                (dateTime) => (
                  <div
                    key={dateTime.toISODate()}
                    className="p-4 text-center text-sm font-semibold text-gray-700 border-r border-gray-200 last:border-r-0"
                  >
                    <div>
                      {dateTime.toFormat(
                        'EEE'
                      )}
                    </div>

                    <div className="text-xs font-normal text-gray-500 mt-1">
                      {
                        dateTime.day
                      }
                    </div>
                  </div>
                )
              )}
            </div>

            <div
              className="overflow-y-auto"
              style={{
                maxHeight:
                  '600px',
              }}
            >
              <div className="grid grid-cols-8">
                <div className="border-r border-gray-200 bg-white sticky left-0 z-10">
                  {timeSlots.map(
                    (slot) => (
                      <div
                        key={
                          slot.hour24
                        }
                        className="border-b border-gray-200 h-16 flex items-start justify-end pr-3 pt-1"
                      >
                        <span className="text-xs text-gray-500 font-medium">
                          {
                            slot.label
                          }
                        </span>
                      </div>
                    )
                  )}
                </div>

                {weekDates.map(
                  (dateTime) => {
                    const isoDate =
                      dateTime.toISODate();

                    const dayBookings =
                      getBookingsForDate(
                        isoDate
                      );

                    return (
                      <div
                        key={
                          isoDate
                        }
                        className="border-r border-gray-200 last:border-r-0 relative"
                        style={{
                          minHeight: `${
                            (endMinutes -
                              startMinutes) *
                            pxPerMinute
                          }px`,
                        }}
                      >
                        {timeSlots.map(
                          (slot) => (
                            <div
                              key={`${isoDate}-${slot.hour24}`}
                              className="border-b border-gray-200 relative cursor-pointer hover:bg-blue-50/40 transition-colors"
                              style={{
                                minHeight: `${hourHeight}px`,

                                backgroundImage:
                                  'repeating-linear-gradient(to bottom, transparent, transparent 31px, rgba(229, 231, 235, 0.4) 31px, rgba(229, 231, 235, 0.4) 32px)',
                              }}
                              onClick={() =>
                                handleSlotSelection(
                                  dateTime.set({
                                    hour:
                                      slot.hour24,
                                    minute: 0,
                                  })
                                )
                              }
                            />
                          )
                        )}

                        {dayBookings.map(
                          (
                            event,
                            idx
                          ) => {
                            const meta =
                              event.calendarMeta;

                            if (!meta) {
                              return null;
                            }

                            if (
                              meta.startMinutes <
                                startMinutes ||
                              meta.startMinutes >=
                                endMinutes
                            ) {
                              return null;
                            }

                            const top =
                              (meta.startMinutes -
                                startMinutes) *
                              pxPerMinute;

                            const height =
                              Math.max(
                                (
                                  meta.endMinutes -
                                  meta.startMinutes
                                ) *
                                  pxPerMinute,
                                32
                              );

                            const past =
                              isPastBooking(
                                event
                              );

                            const isTemporary =
                              event.is_temporary;

                            return (
                              <button
                                key={`${event.id}-${idx}`}
                                disabled={past}
                                onClick={(e) => {
                                  e.stopPropagation();

                                  handleEventSelect(
                                    event
                                  );
                                }}
                                className={`absolute left-1 right-1 text-left rounded px-2 py-1 shadow-sm focus:outline-none ${
                                  past
                                    ? 'bg-gray-100 border-l-2 border-gray-300 text-gray-400 cursor-not-allowed'
                                    : event.is_canceled
                                      ? 'bg-red-50/70 border-l-2 border-red-300 hover:bg-red-50'
                                      : isTemporary
                                        ? 'bg-yellow-100/50 border-l-2 border-yellow-400 hover:bg-yellow-100/70'
                                        : 'bg-blue-50 border-l-2 border-blue-600 hover:bg-blue-100'
                                }`}
                                style={{
                                  top: `${top}px`,
                                  height: `${height}px`,
                                }}
                              >
                                <div className="text-xs font-semibold truncate">
                                  {
                                    event.customer_name ||
                                    'Unknown User'
                                  }
                                </div>

                                <div className="text-xs text-gray-600 truncate">
                                  {
                                    event.resource
                                      ?.name ||
                                    'Resource'
                                  }
                                </div>

                                <div className="text-xs text-gray-600 truncate">
                                  {
                                    meta.displayTimeLabel
                                  }
                                </div>
                              </button>
                            );
                          }
                        )}
                      </div>
                    );
                  }
                )}
              </div>
            </div>
          </div>
        </div>
      );
    };

  const renderDayView =
    () => {
      const isoDate =
        currentViewDate.toISODate();

      const dayBookings =
        getBookingsForDate(
          isoDate
        );

      return (
        <div className="p-6">
          <div className="text-center mb-6">
            <h3 className="text-2xl font-semibold text-gray-900">
              {currentViewDate.toFormat(
                'EEEE'
              )}
            </h3>

            <p className="text-lg text-gray-600">
              {currentViewDate.toFormat(
                'MMMM d, yyyy'
              )}
            </p>
          </div>

          <div className="max-w-2xl mx-auto space-y-3">
            {dayBookings.length ===
            0 ? (
              <div className="text-center py-10 text-gray-500">
                No bookings for this day
              </div>
            ) : (
              dayBookings.map(
                (
                  event,
                  idx
                ) => {
                  const past =
                    isPastBooking(
                      event
                    );

                  const isTemporary =
                    event.is_temporary;

                  return (
                    <button
                      key={`${event.id}-${idx}`}
                      disabled={past}
                      onClick={() =>
                        handleEventSelect(
                          event
                        )
                      }
                      className={`w-full text-left px-4 py-3 rounded focus:outline-none ${
                        past
                          ? 'bg-gray-100 border-l-4 border-gray-300 text-gray-400 cursor-not-allowed'
                          : event.is_canceled
                            ? 'bg-red-50/70 border-l-4 border-red-300 hover:bg-red-50'
                            : isTemporary
                              ? 'bg-yellow-100/50 border-l-4 border-yellow-400 hover:bg-yellow-100/70'
                              : 'bg-blue-50 border-l-4 border-blue-600 hover:bg-blue-100'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-gray-900">
                          {
                            event.customer_name ||
                            'Unknown User'
                          }
                        </span>

                        <span className="text-sm text-gray-600">
                          {
                            event
                              .calendarMeta
                              ?.displayTimeLabel
                          }
                        </span>
                      </div>

                      <div className="text-sm text-gray-600 mt-1">
                        {
                          event.resource
                            ?.name ||
                          'Resource'
                        }
                      </div>
                    </button>
                  );
                }
              )
            )}
          </div>
        </div>
      );
    };

  const renderDateRangeView = () => {
    const groupedBookings = bookings.reduce(
      (groups, booking) => {
        const date =
          booking.calendarMeta?.displayDate;

        if (!date) {
          return groups;
        }

        if (!groups[date]) {
          groups[date] = [];
        }

        groups[date].push(booking);

        return groups;
      },
      {}
    );

    const sortedDates =
      Object.keys(groupedBookings).sort(
        (a, b) =>
          DateTime.fromISO(a).toMillis() -
          DateTime.fromISO(b).toMillis()
      );

    return (
      <div className="p-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                From
              </label>

              <input
                type="date"
                value={dateRange.from}
                onChange={(e) =>
                  setDateRange((prev) => ({
                    ...prev,
                    from: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                To
              </label>

              <input
                type="date"
                value={dateRange.to}
                min={dateRange.from}
                onChange={(e) =>
                  setDateRange((prev) => ({
                    ...prev,
                    to: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>
          </div>

          <div className="text-center text-sm text-gray-500 mb-6">
            Showing bookings from{' '}

            <span className="font-medium text-gray-700">
              {DateTime
                .fromISO(dateRange.from)
                .toFormat('MMM d, yyyy')}
            </span>

            {' '}to{' '}

            <span className="font-medium text-gray-700">
              {DateTime
                .fromISO(dateRange.to)
                .toFormat('MMM d, yyyy')}
            </span>
          </div>

          {sortedDates.length === 0 ? (
            <div className="text-center py-10 text-gray-500">
              No bookings for this date range
            </div>
          ) : (
            <div className="space-y-3">
              {sortedDates.map((date) => {
                const dateTime =
                  DateTime.fromISO(date);

                const dayBookings =
                  groupedBookings[date];

                return (
                  <div
                    key={date}
                    className="flex border-b border-gray-200 pb-3"
                  >
                    <div className="w-16 sm:w-20 flex-shrink-0 text-center pt-2">
                      <div className="text-sm text-gray-500 font-medium">
                        {dateTime.toFormat('EEE')}
                      </div>

                      <div className="text-2xl font-semibold text-gray-900">
                        {dateTime.toFormat('dd')}
                      </div>

                      <div className="text-xs text-gray-500">
                        {dateTime.toFormat('MMM')}
                      </div>
                    </div>

                    <div className="flex-1 space-y-2">
                      {dayBookings.map(
                        (event, idx) => {
                          const past =
                            isPastBooking(event);

                          const isTemporary =
                            event.is_temporary;

                          const isCancelled =
                            event.is_canceled;

                          return (
                            <button
                              key={`${event.id}-${idx}`}
                              disabled={past}
                              onClick={() =>
                                handleEventSelect(event)
                              }
                              className={`w-full text-left px-4 py-3 rounded-lg border transition-colors focus:outline-none ${
                                past
                                  ? 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed'
                                  : isCancelled
                                    ? 'bg-red-50/70 border-red-100 hover:bg-red-50 text-gray-900'
                                    : isTemporary
                                      ? 'bg-yellow-50 border-yellow-300 hover:bg-yellow-100 text-gray-900'
                                      : 'bg-blue-50 border-blue-200 hover:bg-blue-100 text-gray-900'
                              }`}
                            >
                              <div className="flex justify-between items-center gap-4">
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className="font-semibold truncate">
                                    {event.customer_name ||
                                      'Unknown User'}
                                  </span>

                                  {isCancelled && (
                                    <span className="flex-shrink-0 px-2 py-0.5 text-[10px] font-semibold rounded-full bg-red-100 text-red-700">
                                      Cancelled
                                    </span>
                                  )}

                                  {!isCancelled &&
                                    isTemporary && (
                                      <span className="flex-shrink-0 px-2 py-0.5 text-[10px] font-semibold rounded-full bg-yellow-200 text-yellow-800">
                                        Tentative
                                      </span>
                                    )}
                                </div>

                                <span className="text-sm text-gray-600 flex-shrink-0">
                                  {
                                    event.calendarMeta
                                      ?.displayTimeLabel
                                  }
                                </span>
                              </div>

                              <div className="text-sm text-gray-600 mt-1">
                                {event.resource?.name ||
                                  'Resource'}
                              </div>

                              {event.location?.name && (
                                <div className="text-xs text-gray-500 mt-1">
                                  {event.location.name}
                                </div>
                              )}
                            </button>
                          );
                        }
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  const handleSidePanelSave =
    async () => {
      calendarCacheRef.current =
        {};

      setCalendarCache({});

      dateRangeCacheRef.current.clear();

      await refreshCurrentView();

      setIsPanelCollapsed(
        true
      );

      setSelectedBooking(
        null
      );
    };

  const handleSidePanelDelete =
    async () => {
      calendarCacheRef.current =
        {};

      setCalendarCache({});

      dateRangeCacheRef.current.clear();

      await refreshCurrentView();

      setIsPanelCollapsed(
        true
      );

      setSelectedBooking(
        null
      );
    };

  const handleNewBooking =
    () => {
      setSelectedBooking(
        null
      );

      setSelectedDate(
        new Date()
      );

      setSelectedTimeSlot(
        null
      );

      setShowSidePanel(
        true
      );
    };

  const calendarViews = [
    'Month',
    'Week',
    'Day',
    'Date Range',
  ];

  return (
    <div className="space-y-6 relative h-full">
      <div
        className="bg-white rounded-lg border border-gray-200 flex relative overflow-hidden"
        style={{
          height:
            'calc(100vh - 220px)',
        }}
      >
        <div
          className="flex-1 flex flex-col overflow-hidden transition-all duration-300 max-lg:!mr-0"
          style={{
            marginRight:
              showSidePanel
                ? isPanelCollapsed
                  ? '90px'
                  : '424px'
                : '0',
          }}
        >
          <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-wrap items-center gap-2">
                {calendarViews.map(
                  (type) => (
                    <button
                      key={type}
                      onClick={() =>
                        setCalendarView(
                          type
                        )
                      }
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                        calendarView ===
                        type
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {type}
                    </button>
                  )
                )}
              </div>

              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2 w-full lg:w-auto lg:ml-20 mt-4 lg:mt-0">
                <button
                  onClick={
                    goToPrevious
                  }
                  className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                  aria-label="Previous period"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                <span className="text-lg font-semibold text-gray-900 min-w-[180px] text-center">
                  {
                    calendarHeaderText
                  }
                </span>

                <button
                  onClick={
                    goToNext
                  }
                  className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                  aria-label="Next period"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          <div className="px-6 py-3 border-b border-gray-200 flex-shrink-0 bg-gray-50">
            <div className="flex items-center gap-3">
              <button
                type="button"
                role="switch"
                aria-checked={
                  showCancelledBookings
                }
                onClick={() =>
                  setShowCancelledBookings(
                    (prev) =>
                      !prev
                  )
                }
                className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 cursor-pointer ${
                  showCancelledBookings
                    ? 'bg-blue-600'
                    : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    showCancelledBookings
                      ? 'translate-x-5'
                      : 'translate-x-0'
                  }`}
                />
              </button>

              <span className="text-sm font-medium text-gray-700">
                Show Cancelled Bookings
              </span>
            </div>
          </div>

          <div className="flex-1 overflow-auto">
            {loading ? (
              <div className="flex justify-center py-12 text-gray-500">
                Loading calendar...
              </div>
            ) : error ? (
              <div className="flex justify-center py-12 text-red-500">
                {error}
              </div>
            ) : (
              <>
                {calendarView ===
                  'Month' &&
                  renderMonthView()}

                {calendarView ===
                  'Week' &&
                  renderWeekView()}

                {calendarView ===
                  'Day' &&
                  renderDayView()}

                {calendarView ===
                  'Date Range' &&
                  renderDateRangeView()}
              </>
            )}
          </div>
        </div>

        {showSidePanel &&
          !isPanelCollapsed && (
            <div
              className="lg:hidden absolute inset-0 bg-black/30 z-40"
              onClick={() =>
                setShowSidePanel(
                  false
                )
              }
            />
          )}

        {showSidePanel && (
          <BookingSidePanel
            isOpen={
              showSidePanel
            }
            onClose={() =>
              setShowSidePanel(
                false
              )
            }
            booking={
              selectedBooking
            }
            selectedDate={
              selectedDate
            }
            selectedTimeSlot={
              selectedTimeSlot
            }
            onSave={
              handleSidePanelSave
            }
            onDelete={
              handleSidePanelDelete
            }
            onNewBooking={
              handleNewBooking
            }
            isCollapsed={
              isPanelCollapsed
            }
            onToggleCollapse={() =>
              setIsPanelCollapsed(
                (prev) =>
                  !prev
              )
            }
          />
        )}

        {showCancelledBookingModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <AlertCircle className="w-6 h-6 text-red-500" />
                  </div>

                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Cannot Edit Booking
                    </h3>

                    <p className="text-gray-600">
                      This is a cancelled booking and cannot be edited.
                    </p>
                  </div>

                  <button
                    onClick={() =>
                      setShowCancelledBookingModal(
                        false
                      )
                    }
                    className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={() =>
                      setShowCancelledBookingModal(
                        false
                      )
                    }
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}